/**
 * Offline Map Cache Service
 * 
 * Downloads and caches map tiles/images in IndexedDB
 * for offline access in low-signal oilfield areas
 * 
 * Usage:
 * - Cache entire work areas (Hobbs, Carlsbad, etc.)
 * - Cache individual well static maps
 * - Check cache status
 * - Manage storage space
 */

const DB_NAME = 'ZiaWellNavOfflineMaps';
const DB_VERSION = 1;
const TILES_STORE = 'mapTiles';
const STATIC_MAPS_STORE = 'staticMaps';
const META_STORE = 'cacheMeta';

interface CachedTile {
  key: string;
  blob: Blob;
  timestamp: number;
  area?: string;
  z: number;
  x: number;
  y: number;
}

interface CachedStaticMap {
  key: string;
  blob: Blob;
  timestamp: number;
  wellId?: string;
  wellName?: string;
  lat: number;
  lng: number;
}

interface CacheMeta {
  area: string;
  cachedAt: number;
  tileCount: number;
  sizeMB: number;
  zoomRange: { min: number; max: number };
}

interface CacheProgress {
  total: number;
  completed: number;
  failed: number;
  sizeMB: number;
  currentUrl?: string;
  status: 'idle' | 'downloading' | 'complete' | 'error';
}

type ProgressCallback = (progress: CacheProgress) => void;

class OfflineMapCacheService {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  /**
   * Initialize the IndexedDB database
   */
  async init(): Promise<void> {
    if (this.db) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('Failed to open offline maps database:', request.error);
        reject(request.error);
      };
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Map tiles store
        if (!db.objectStoreNames.contains(TILES_STORE)) {
          const tilesStore = db.createObjectStore(TILES_STORE, { keyPath: 'key' });
          tilesStore.createIndex('area', 'area', { unique: false });
          tilesStore.createIndex('timestamp', 'timestamp', { unique: false });
        }
        
        // Static maps store (well images)
        if (!db.objectStoreNames.contains(STATIC_MAPS_STORE)) {
          const staticStore = db.createObjectStore(STATIC_MAPS_STORE, { keyPath: 'key' });
          staticStore.createIndex('wellId', 'wellId', { unique: false });
          staticStore.createIndex('timestamp', 'timestamp', { unique: false });
        }
        
        // Cache metadata store
        if (!db.objectStoreNames.contains(META_STORE)) {
          db.createObjectStore(META_STORE, { keyPath: 'area' });
        }
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('✅ Offline maps database initialized');
        resolve();
      };
    });

    return this.initPromise;
  }

  /**
   * Cache a single tile
   */
  async cacheTile(
    url: string, 
    z: number, 
    x: number, 
    y: number, 
    area?: string
  ): Promise<{ success: boolean; size: number }> {
    await this.init();

    try {
      const response = await fetch(url);
      if (!response.ok) {
        return { success: false, size: 0 };
      }

      const blob = await response.blob();
      const tile: CachedTile = {
        key: `${z}/${x}/${y}`,
        blob,
        timestamp: Date.now(),
        area,
        z,
        x,
        y,
      };

      return new Promise((resolve, reject) => {
        const tx = this.db!.transaction(TILES_STORE, 'readwrite');
        const store = tx.objectStore(TILES_STORE);
        const request = store.put(tile);
        request.onsuccess = () => resolve({ success: true, size: blob.size });
        request.onerror = () => reject({ success: false, size: 0 });
      });
    } catch (error) {
      console.error('Failed to cache tile:', url, error);
      return { success: false, size: 0 };
    }
  }

  /**
   * Get a cached tile
   */
  async getCachedTile(z: number, x: number, y: number): Promise<Blob | null> {
    await this.init();

    return new Promise((resolve) => {
      const tx = this.db!.transaction(TILES_STORE, 'readonly');
      const store = tx.objectStore(TILES_STORE);
      const request = store.get(`${z}/${x}/${y}`);
      request.onsuccess = () => {
        const tile = request.result as CachedTile | undefined;
        resolve(tile?.blob || null);
      };
      request.onerror = () => resolve(null);
    });
  }

  /**
   * Cache a static map image (for well locations)
   */
  async cacheStaticMap(
    url: string,
    wellId: string,
    wellName: string,
    lat: number,
    lng: number
  ): Promise<{ success: boolean; size: number }> {
    await this.init();

    try {
      const response = await fetch(url);
      if (!response.ok) {
        return { success: false, size: 0 };
      }

      const blob = await response.blob();
      const staticMap: CachedStaticMap = {
        key: `well-${wellId}`,
        blob,
        timestamp: Date.now(),
        wellId,
        wellName,
        lat,
        lng,
      };

      return new Promise((resolve, reject) => {
        const tx = this.db!.transaction(STATIC_MAPS_STORE, 'readwrite');
        const store = tx.objectStore(STATIC_MAPS_STORE);
        const request = store.put(staticMap);
        request.onsuccess = () => resolve({ success: true, size: blob.size });
        request.onerror = () => reject({ success: false, size: 0 });
      });
    } catch (error) {
      console.error('Failed to cache static map:', url, error);
      return { success: false, size: 0 };
    }
  }

  /**
   * Get a cached static map
   */
  async getCachedStaticMap(wellId: string): Promise<Blob | null> {
    await this.init();

    return new Promise((resolve) => {
      const tx = this.db!.transaction(STATIC_MAPS_STORE, 'readonly');
      const store = tx.objectStore(STATIC_MAPS_STORE);
      const request = store.get(`well-${wellId}`);
      request.onsuccess = () => {
        const map = request.result as CachedStaticMap | undefined;
        resolve(map?.blob || null);
      };
      request.onerror = () => resolve(null);
    });
  }

  /**
   * Cache all tiles for a work area
   */
  async cacheArea(
    areaId: string,
    onProgress?: ProgressCallback
  ): Promise<{ success: boolean; cached: number; failed: number; sizeMB: number }> {
    await this.init();

    // Fetch manifest from API
    const response = await fetch(`/api/offline/map-cache?action=manifest&area=${areaId}&minZoom=12&maxZoom=15`);
    
    if (!response.ok) {
      const error = await response.json();
      console.error('Failed to fetch manifest:', error);
      return { success: false, cached: 0, failed: 0, sizeMB: 0 };
    }

    const manifest = await response.json();

    if (!manifest.tiles || manifest.tiles.length === 0) {
      return { success: false, cached: 0, failed: 0, sizeMB: 0 };
    }

    const progress: CacheProgress = {
      total: manifest.tiles.length,
      completed: 0,
      failed: 0,
      sizeMB: 0,
      status: 'downloading',
    };

    onProgress?.(progress);

    // Download tiles in batches to avoid overwhelming the browser
    const batchSize = 6; // Concurrent downloads
    const tiles = manifest.tiles as Array<{ url: string; z: number; x: number; y: number }>;

    for (let i = 0; i < tiles.length; i += batchSize) {
      const batch = tiles.slice(i, i + batchSize);
      
      const results = await Promise.all(
        batch.map(async (tile) => {
          progress.currentUrl = tile.url;
          const result = await this.cacheTile(tile.url, tile.z, tile.x, tile.y, areaId);
          return result;
        })
      );

      results.forEach((result) => {
        if (result.success) {
          progress.completed++;
          progress.sizeMB += result.size / 1024 / 1024;
        } else {
          progress.failed++;
        }
      });

      onProgress?.({ ...progress });

      // Small delay to prevent browser throttling
      await new Promise((r) => setTimeout(r, 50));
    }

    progress.status = 'complete';
    progress.sizeMB = Math.round(progress.sizeMB * 100) / 100;
    onProgress?.(progress);

    // Save metadata
    const meta: CacheMeta = {
      area: areaId,
      cachedAt: Date.now(),
      tileCount: progress.completed,
      sizeMB: progress.sizeMB,
      zoomRange: manifest.zoomRange,
    };

    await this.saveCacheMeta(meta);

    return {
      success: progress.failed === 0,
      cached: progress.completed,
      failed: progress.failed,
      sizeMB: progress.sizeMB,
    };
  }

  /**
   * Cache wells for an area
   */
  async cacheAreaWells(
    areaId: string,
    onProgress?: ProgressCallback
  ): Promise<{ success: boolean; cached: number; failed: number }> {
    await this.init();

    // Fetch well maps from API
    const response = await fetch(`/api/offline/map-cache?action=wellMaps&area=${areaId}`);
    
    if (!response.ok) {
      return { success: false, cached: 0, failed: 0 };
    }

    const data = await response.json();

    if (!data.wells || data.wells.length === 0) {
      return { success: true, cached: 0, failed: 0 };
    }

    const progress: CacheProgress = {
      total: data.wells.length,
      completed: 0,
      failed: 0,
      sizeMB: 0,
      status: 'downloading',
    };

    onProgress?.(progress);

    // Download well maps
    for (const well of data.wells) {
      progress.currentUrl = well.staticMapUrl;
      const result = await this.cacheStaticMap(
        well.staticMapUrl,
        well.id,
        well.name,
        well.lat,
        well.lng
      );

      if (result.success) {
        progress.completed++;
        progress.sizeMB += result.size / 1024 / 1024;
      } else {
        progress.failed++;
      }

      onProgress?.({ ...progress });
    }

    progress.status = 'complete';
    onProgress?.(progress);

    return {
      success: progress.failed === 0,
      cached: progress.completed,
      failed: progress.failed,
    };
  }

  /**
   * Save cache metadata
   */
  private async saveCacheMeta(meta: CacheMeta): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(META_STORE, 'readwrite');
      const store = tx.objectStore(META_STORE);
      const request = store.put(meta);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get cache statistics
   */
  async getCacheStats(): Promise<{
    tileCount: number;
    staticMapCount: number;
    totalSizeMB: number;
    areas: CacheMeta[];
  }> {
    await this.init();

    const tilesPromise = new Promise<CachedTile[]>((resolve) => {
      const tx = this.db!.transaction(TILES_STORE, 'readonly');
      const store = tx.objectStore(TILES_STORE);
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => resolve([]);
    });

    const staticMapsPromise = new Promise<CachedStaticMap[]>((resolve) => {
      const tx = this.db!.transaction(STATIC_MAPS_STORE, 'readonly');
      const store = tx.objectStore(STATIC_MAPS_STORE);
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => resolve([]);
    });

    const metaPromise = new Promise<CacheMeta[]>((resolve) => {
      const tx = this.db!.transaction(META_STORE, 'readonly');
      const store = tx.objectStore(META_STORE);
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => resolve([]);
    });

    const [tiles, staticMaps, areas] = await Promise.all([
      tilesPromise,
      staticMapsPromise,
      metaPromise,
    ]);

    const tileSize = tiles.reduce((sum, t) => sum + t.blob.size, 0);
    const staticMapSize = staticMaps.reduce((sum, m) => sum + m.blob.size, 0);

    return {
      tileCount: tiles.length,
      staticMapCount: staticMaps.length,
      totalSizeMB: Math.round((tileSize + staticMapSize) / 1024 / 1024 * 100) / 100,
      areas,
    };
  }

  /**
   * Clear cache for a specific area
   */
  async clearArea(areaId: string): Promise<void> {
    await this.init();

    // Clear tiles for this area
    const tx1 = this.db!.transaction(TILES_STORE, 'readwrite');
    const tilesStore = tx1.objectStore(TILES_STORE);
    const areaIndex = tilesStore.index('area');
    const request = areaIndex.openCursor(IDBKeyRange.only(areaId));

    request.onsuccess = () => {
      const cursor = request.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };

    // Clear metadata
    const tx2 = this.db!.transaction(META_STORE, 'readwrite');
    tx2.objectStore(META_STORE).delete(areaId);
  }

  /**
   * Clear all cached data
   */
  async clearAll(): Promise<void> {
    await this.init();

    const tx = this.db!.transaction([TILES_STORE, STATIC_MAPS_STORE, META_STORE], 'readwrite');
    tx.objectStore(TILES_STORE).clear();
    tx.objectStore(STATIC_MAPS_STORE).clear();
    tx.objectStore(META_STORE).clear();

    console.log('🗑️ All offline map cache cleared');
  }

  /**
   * Check if we have cached tiles for an area
   */
  async isAreaCached(areaId: string): Promise<boolean> {
    await this.init();

    return new Promise((resolve) => {
      const tx = this.db!.transaction(META_STORE, 'readonly');
      const store = tx.objectStore(META_STORE);
      const request = store.get(areaId);
      request.onsuccess = () => resolve(!!request.result);
      request.onerror = () => resolve(false);
    });
  }

  /**
   * Find nearby areas that should be cached based on user location
   */
  async findNearbyAreas(lat: number, lng: number): Promise<string[]> {
    try {
      const response = await fetch(`/api/offline/map-cache?action=nearby&lat=${lat}&lng=${lng}&radius=100`);
      if (!response.ok) return [];
      const data = await response.json();
      return data.nearbyAreas?.map((a: { id: string }) => a.id) || [];
    } catch {
      return [];
    }
  }

  /**
   * Automatically cache nearby areas based on user location
   * Returns areas that were cached (empty if already cached or offline)
   */
  async autoCacheNearby(
    lat: number, 
    lng: number,
    onProgress?: (areaId: string, progress: CacheProgress) => void,
    onComplete?: (results: { areaId: string; success: boolean; sizeMB: number }[]) => void
  ): Promise<{ areaId: string; success: boolean; sizeMB: number }[]> {
    await this.init();

    // Check if auto-cache was recently run (within 24 hours)
    const lastAutoCacheKey = 'ziawellnav_last_autocache';
    const lastAutoCacheLocation = 'ziawellnav_last_autocache_location';
    const lastRun = localStorage.getItem(lastAutoCacheKey);
    const lastLocation = localStorage.getItem(lastAutoCacheLocation);
    
    const now = Date.now();
    const dayMs = 24 * 60 * 60 * 1000;
    
    // Skip if ran recently from similar location
    if (lastRun && lastLocation) {
      const timeSince = now - parseInt(lastRun, 10);
      const [lastLat, lastLng] = lastLocation.split(',').map(Number);
      const distanceKm = this.haversineDistance(lat, lng, lastLat, lastLng);
      
      // Skip if less than 24 hours and within 50km of last check
      if (timeSince < dayMs && distanceKm < 50) {
        console.log('⏭️ Auto-cache: Skipping (recently checked from nearby location)');
        return [];
      }
    }

    console.log('🔄 Auto-cache: Checking for nearby areas to cache...');
    
    // Find nearby areas
    const nearbyAreaIds = await this.findNearbyAreas(lat, lng);
    
    if (nearbyAreaIds.length === 0) {
      console.log('📍 Auto-cache: No work areas near current location');
      localStorage.setItem(lastAutoCacheKey, now.toString());
      localStorage.setItem(lastAutoCacheLocation, `${lat},${lng}`);
      return [];
    }

    // Filter out already cached areas
    const uncachedAreas: string[] = [];
    for (const areaId of nearbyAreaIds) {
      const isCached = await this.isAreaCached(areaId);
      if (!isCached) {
        uncachedAreas.push(areaId);
      }
    }

    if (uncachedAreas.length === 0) {
      console.log('✅ Auto-cache: All nearby areas already cached');
      localStorage.setItem(lastAutoCacheKey, now.toString());
      localStorage.setItem(lastAutoCacheLocation, `${lat},${lng}`);
      return [];
    }

    console.log(`📥 Auto-cache: Caching ${uncachedAreas.length} area(s): ${uncachedAreas.join(', ')}`);

    // Cache each uncached area
    const results: { areaId: string; success: boolean; sizeMB: number }[] = [];
    
    for (const areaId of uncachedAreas) {
      try {
        const result = await this.cacheArea(areaId, (progress) => {
          onProgress?.(areaId, progress);
        });
        
        results.push({
          areaId,
          success: result.success,
          sizeMB: result.sizeMB,
        });
      } catch (error) {
        console.error(`Failed to cache area ${areaId}:`, error);
        results.push({ areaId, success: false, sizeMB: 0 });
      }
    }

    // Save last run info
    localStorage.setItem(lastAutoCacheKey, now.toString());
    localStorage.setItem(lastAutoCacheLocation, `${lat},${lng}`);

    onComplete?.(results);
    
    const totalMB = results.reduce((sum, r) => sum + r.sizeMB, 0);
    const successCount = results.filter(r => r.success).length;
    console.log(`✅ Auto-cache: Completed - ${successCount}/${results.length} areas cached (${totalMB.toFixed(1)} MB)`);

    return results;
  }

  /**
   * Calculate distance between two coordinates (Haversine formula)
   */
  private haversineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }
}

// Export singleton instance
export const offlineMapCache = new OfflineMapCacheService();

// Export types
export type { CacheProgress, CacheMeta };
